import { Injectable, Optional } from '@angular/core';
import { MaskApplierService } from 'ngx-mask';

interface AttributeLike {
    value?: string;
    options?: any[];
}

@Injectable({ providedIn: 'root' })
export class CurrencyService {
    constructor(@Optional() private maskService?: MaskApplierService) {}

    public getCurrencyMaskText(value: AttributeLike | string, isInputText: boolean = false): string {
        
        if (typeof value === 'string') {
            return this.getMaskForValue(value);
        }
        
        if (value && typeof value === 'object' && 'value' in value) {
            if (isInputText) {
                return this.getCurrencyMaskForInput();
            }
            
            const attrValue = value.value;
            const isNegative = attrValue && attrValue.toString().startsWith('-');
            const absValue = isNegative ? attrValue.toString().substring(1) : attrValue;
            
            return this.getMaskForValue(absValue);
        }
        
        return "";
    }
    
    private getMaskForValue(value: string): string {
        const isNegative = value && value.toString().startsWith('-');
        const valueWithoutSign = isNegative ? value.toString().substring(1) : value;
        
        const currencyString = this.getCurrencyStringNoSeparator(valueWithoutSign);
        const currencyStringLength = currencyString ? currencyString.length : 0;
        
        let mask = "";
        if(currencyString && currencyStringLength == 3) {
            mask = "0,00";
        } else if(currencyString && currencyStringLength == 4) {
            mask = "00,00";
        } else if(currencyString && currencyStringLength == 5) {
            mask = "000,00";
        } else if(currencyString && currencyStringLength == 6) {
            mask = "0.000,00";
        } else if(currencyString && currencyStringLength == 7) {
            mask = "00.000,00";
        } else if(currencyString && currencyStringLength == 8) {
            mask = "000.000,00";
        } else if(currencyString && currencyStringLength == 9) {
            mask = "0.000.000,00";
        } else if(currencyString && currencyStringLength == 10) {
            mask = "00.000.000,00";
        } else if(currencyString && currencyStringLength == 11) {
            mask = "000.000.000,00";
        } else if(currencyString && currencyStringLength == 12) {
            mask = "0.000.000.000,00";
        } else if(currencyString && currencyStringLength == 13) {
            mask = "00.000.000.000,00";
        } else if(currencyString && currencyStringLength == 14) {
            mask = "000.000.000.000,00";
        } else if(currencyString && currencyStringLength == 15) {
            mask = "0.000.000.000.000,00";
        }

        return mask;
    }
    
    private getCurrencyMaskForInput(): string {
        // Alternativa vazia permite campo sem dígitos (somente prefixo)
        return "||0,00||00,00||000,00||0.000,00||00.000,00||000.000,00||0.000.000,00||00.000.000,00||000.000.000,00||0.000.000.000,00||00.000.000.000,00||000.000.000.000,00";
    }

    public getCurrencyStringNoSeparatorNumber(currencyString: string): number {
        return +this.getCurrencyStringNoSeparator(currencyString);
    }

    public getCurrencyStringNoSeparator(currencyString: string): string {
        if (!currencyString) return "";
        
        const isNegative = currencyString.toString().startsWith('-');
        const valueWithoutSign = isNegative ? currencyString.toString().substring(1) : currencyString;
        
        let result = this.getCurrencyStringAsNumberString(valueWithoutSign);
        result = result.replace(/[.,\s]/g, '');
        
        return isNegative ? '-' + result : result;
    }

    public getCurrencyStringAsNumberString(currencyString: string): string {
        if (currencyString) {
            let result = currencyString.replace('R$', '').trim();
        
            const lastCommaIndex = result.lastIndexOf(',');
            const lastDotIndex = result.lastIndexOf('.');
        
            let decimalSeparatorIndex = -1;
            if (lastCommaIndex > lastDotIndex) {
                decimalSeparatorIndex = lastCommaIndex;
            } else {
                decimalSeparatorIndex = lastDotIndex;
            }
        
            if (decimalSeparatorIndex > -1) {
                const integerPart = result.substring(0, decimalSeparatorIndex);
                let decimalPart = result.substring(decimalSeparatorIndex + 1);
        
                if (decimalPart.length == 1) {
                    decimalPart = decimalPart + '0';
                } else if (decimalPart.length > 2) {
                    decimalPart = decimalPart.slice(0, 2);
                }
        
                result = integerPart + '.' + decimalPart;
            }
        
            return result;
        }
      
        return "";
    }

    public getCurrencyStringAsNumberNormalized(currencyString: string): string {
        if (currencyString) {
            const isNegative = currencyString.toString().startsWith('-');
            const valueWithoutSign = isNegative ? currencyString.toString().substring(1) : currencyString;
            
            let result = valueWithoutSign.replace('R$', '').trim();
        
            const lastCommaIndex = result.lastIndexOf(',');
            const lastDotIndex = result.lastIndexOf('.');
        
            let decimalSeparatorIndex = -1;
            if (lastCommaIndex > lastDotIndex) {
                decimalSeparatorIndex = lastCommaIndex;
            } else {
                decimalSeparatorIndex = lastDotIndex;
            }
        
            if (decimalSeparatorIndex > -1) {
                const integerPart = result.substring(0, decimalSeparatorIndex);
                let decimalPart = result.substring(decimalSeparatorIndex + 1);
        
                if (decimalPart.length == 1) {
                    decimalPart = decimalPart + '0';
                } else if (decimalPart.length > 2) {
                    decimalPart = decimalPart.slice(0, 2);
                }
        
                result = integerPart + '.' + decimalPart;
            } else {
                result = result + '.00';
            }
        
            return isNegative ? '-' + result : result;
        }
      
        return "";
    }

    public formatCurrencyValue(value: string): string {
        if (!value) return "R$ 0,00";
        
        const isNegative = value.toString().startsWith('-');
        
        const absValue = isNegative ? value.toString().substring(1) : value;
        
        const mask = this.getMaskForValue(absValue);
        
        if (this.maskService) {
            const formattedValue = this.maskService.applyMask(absValue, mask || "0,00");
            return isNegative ? "R$ -" + formattedValue : "R$ " + formattedValue;
        } else {
            const formattedValue = this.formatValueWithoutMaskService(absValue);
            return isNegative ? "R$ -" + formattedValue : "R$ " + formattedValue;
        }
    }
    
    public applyMask(value: string, mask: string): string {
        if (!value) return "";

        try {
            if (this.maskService) {
                return this.maskService.applyMask(value, mask);
            }
            
            const numValue = parseFloat(value);
            return numValue.toLocaleString('pt-BR', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
            });
        } catch (e) {
            console.error("Erro ao aplicar máscara:", e);
            return value;
        }
    }
    
    private formatValueWithoutMaskService(value: string): string {
        if (!value) return "0,00";
        
        const numValue = parseFloat(value);
        return numValue.toLocaleString('pt-BR', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        });
    }
}